import React from 'react';
import { Button, Menu, MenuItem, MenuTrigger } from '@chatsdet/ui';
import { ArrowClockwise, DotsThreeVertical, Stop, Trash } from '@phosphor-icons/react';
import type { RunsListRow } from '../../../../features/workflows/useWorkflowTestExecutions';

interface TestRunActionsMenuProps {
  execution: RunsListRow;
  isCancelPending: boolean;
  isDeletePending: boolean;
  /** Native only — acts on `workflow_test_executions`. */
  onCancel: (executionId: string) => void;
  /** Native only — acts on `workflow_test_executions`. */
  onDelete: (executionId: string) => void;
  /**
   * External only. Separate props rather than reusing `onCancel`/`onDelete`
   * because those handlers hit the workflow-execution endpoints, and an external
   * run id passed to either would address a row in the wrong table — which is
   * precisely why external rows used to render no menu at all.
   */
  isExternalCancelPending: boolean;
  isExternalRerunPending: boolean;
  onExternalCancel: (runId: string) => void;
  onExternalRerun: (runId: string) => void;
}

/** The trigger button + popover shell, shared by both branches. */
function MenuShell({
  onAction,
  children,
}: {
  onAction: (key: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex justify-end" onClick={(event) => event.stopPropagation()}>
      <MenuTrigger>
        <Button
          variant="ghost"
          size="sm"
          className="p-2 text-muted-foreground"
          aria-label="Run actions"
        >
          <DotsThreeVertical size={16} weight="bold" />
        </Button>
        <Menu onAction={(key) => onAction(String(key))}>{children}</Menu>
      </MenuTrigger>
    </div>
  );
}

export function TestRunActionsMenu({
  execution,
  isCancelPending,
  isDeletePending,
  onCancel,
  onDelete,
  isExternalCancelPending,
  isExternalRerunPending,
  onExternalCancel,
  onExternalRerun,
}: TestRunActionsMenuProps) {
  // Exhaustive switch on `kind` — never an `else`/ternary fallthrough. Native
  // rows carry `kind?: 'cofactor'` (optional, for back-compat), so both the
  // `'cofactor'` and `undefined` cases fall through to the native menu below;
  // a future third kind added to `RunsListRow` without a case here fails
  // typecheck via the `never` check in `default`, instead of silently
  // offering native rerun/cancel/delete actions that don't apply to it.
  switch (execution.kind) {
    case 'external': {
      // No Delete: an external run's items are the customer's record of what
      // their own suite did, and there is no delete endpoint behind it.
      // Offering one that silently did nothing would be worse than offering
      // none.
      const isActive = execution.status === 'open' || execution.status === 'collecting';
      return (
        <MenuShell
          onAction={(key) => {
            if (key === 'cancel') onExternalCancel(execution.id);
            if (key === 'rerun') onExternalRerun(execution.id);
          }}
        >
          {isActive ? (
            <MenuItem id="cancel" isDisabled={isExternalCancelPending}>
              <Stop size={14} className="mr-2" />
              Cancel
            </MenuItem>
          ) : (
            <MenuItem id="rerun" isDisabled={isExternalRerunPending}>
              <ArrowClockwise size={14} className="mr-2" />
              Run again
            </MenuItem>
          )}
        </MenuShell>
      );
    }
    case 'cofactor':
    case undefined: {
      const isActive = execution.status === 'queued' || execution.status === 'running';
      return (
        <MenuShell
          onAction={(key) => {
            if (key === 'cancel') onCancel(execution.id);
            if (key === 'delete') onDelete(execution.id);
          }}
        >
          {isActive && (
            <MenuItem id="cancel" isDisabled={isCancelPending}>
              <Stop size={14} className="mr-2" />
              Cancel
            </MenuItem>
          )}
          <MenuItem id="delete" isDisabled={isDeletePending}>
            <Trash size={14} className="mr-2" />
            Delete
          </MenuItem>
        </MenuShell>
      );
    }
    default: {
      const exhaustiveCheck: never = execution;
      return exhaustiveCheck;
    }
  }
}
